<template lang="pug">
div
  v-divider(v-if="showDivider")
  .pagination.d-flex.justify-end.py-2
    .direction.d-flex.flex-row.mr-2
      v-btn.previous(@click="goToPrevPage" text :disabled="isInvalidPage || page <= 1")
        v-icon.icon mdi-chevron-left
      .current.px-1
        div
          v-text-field.current-page(
            v-if="!isNextFocusTab"
            ref="paginationInput"
            type="number"
            v-model.number="page"
            min="1"
            outlined
            :max="totalPage"
            hide-details
            hide-spin-buttons
            @focus="handleFocus"
            @blur="$emit('blur')"
            @change="onChange"
            @keydown="handleKeyDown"
            name="pagination"
          )
          v-text-field.current-page(
            v-else
            ref="paginationInput"
            type="number"
            v-model.number="page"
            min="1"
            outlined
            :max="totalPage"
            hide-details
            hide-spin-buttons
            @focus="handleFocus"
            @blur="$emit('blur')"
            @change="onChange"
            @keydown="handleKeyDown"
            v-next-focus-tab:pagination
          )

        div.total-page.pl-1(data-cy="total-page-txt") /{{  totalPage }}
      v-btn.next(@click="goToNextPage" :disabled="isInvalidPage || page >= totalPage" text)
        v-icon.icon mdi-chevron-right
    .items-display
      span(v-if="isUseCustomItemIndex") {{ `${customStartIndex}-${customEndIndex}`}}
      span(v-else) {{ items.length > 1 ? `${fistItemIndex}-${lastItemIndex}`: `${firstItemNum}`}}
      | /{{totalItem}}{{ $t("pagination.items") }}
</template>
<script>
import {
  defineComponent,
  ref,
  getCurrentInstance,
  watch,
  onMounted,
  onUnmounted,
  computed
} from 'vue'
import { isEmpty } from 'lodash'
import CommonTable from '../CommonTable/index'

const Pagination = defineComponent({
  props: {
    currentPage: {
      type: Number,
      default: 1
    },
    totalPage: {
      type: Number,
      default: 1,
      required: true
    },
    totalItem: {
      type: Number,
      required: true
    },
    items: {
      type: Array,
      default: () => [],
      required: true
    },
    pageSize: {
      type: Number,
      required: false,
      default: 10
    },
    isDisabled: {
      type: Boolean,
      required: false,
      default: false
    },
    showDivider: {
      type: Boolean,
      required: false,
      default: true
    },
    isNextFocusTab: {
      type: Boolean,
      required: false,
      default: false
    },
    isUseCustomItemIndex: {
      type: Boolean,
      required: false,
      default: false
    },
    customStartIndex: {
      type: Number,
      required: false,
      default: 0
    },
    customEndIndex: {
      type: Number,
      required: false,
      default: 0
    }
  },
  components: {
    CommonTable
  },
  setup(props, { emit }) {
    const { $toast, $root, $refs } = getCurrentInstance().proxy
    const page = ref(props.currentPage)
    const firstItemNum = ref(0)

    const fistItemIndex = computed(() => {
      return firstItemNum.value
    })

    const lastItemIndex = computed(() => {
      if (isEmpty(props.items)) return 0
      return firstItemNum.value + props.items.length - 1
    })

    const isInvalidPage = computed(() => {
      const p = Number(page.value)
      return !Number.isInteger(p) || p < 1 || p > props.totalPage
    })

    const isChangePage = ref(false)
    const onChange = () => {
      // isChangePage.value = true
      const pageNum = Number(page.value)
      if (!Number.isInteger(pageNum) || pageNum < 1 || pageNum > props.totalPage) {
        $toast.error($root.$t('pagination.invalid_page_num'))
        page.value = props.currentPage
        return
      }
      emit('onChange', pageNum)
    }

    const goToPrevPage = (isArrowUpKey = false) => {
      if (isInvalidPage.value || page.value <= 1) return
      const pageNum = Number(page.value) - 1
      emit('onChange', pageNum, isArrowUpKey)
    }
    const goToNextPage = () => {
      if (isInvalidPage.value || page.value >= props.totalPage) return
      const pageNum = Number(page.value) + 1
      emit('onChange', pageNum)
    }
    const handleFocus = (e) => {
      emit('focus')
    }
    const handleKeyDown = (event) => {
      // if user input number(0-9)
      if (/^[0-9]$/.test(event.key)) {
        isChangePage.value = true
      }
      if (event.key === 'ArrowUp') {
        emit('onArrowUp')
        event.preventDefault()
      }
      if (event.key === 'ArrowDown') {
        emit('onArrowDown')
        event.preventDefault()
      }
      if (event.code === 'Space') {
        emit('onSpace')
        event.preventDefault()
      }
      if (event.code === 'Enter') {
        if (isChangePage.value) {
          isChangePage.value = false
          return
        }
        emit('onEnter')
        event.preventDefault()
      }
      if (event.key === 'Escape') {
        emit('onEsc')
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault() // Prevent cursor movement
        goToPrevPage()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault() // Prevent cursor movement
        goToNextPage()
      }
    }
    watch(
      () => props.currentPage,
      (val) => {
        page.value = val
      }
    )

    watch(
      () => props.items,
      (newItems) => {
        if (props.currentPage && newItems.length > 0)
          firstItemNum.value = props.pageSize * (props.currentPage - 1) + 1
        else firstItemNum.value = 0
      },
      {
        immediate: true
      }
    )

    const functionKey = (e) => {
      if (!props.isDisabled) return
      const functionMapping = new Map()
      functionMapping.set('PageDown', goToPrevPage)
      functionMapping.set('PageUp', goToNextPage)
      const func = functionMapping.get(e.key) || functionMapping.get(e.code)
      if (func && typeof func === 'function') {
        func()
        e.preventDefault()
      }
    }
    const focusPagination = () => {
      $refs.paginationInput.focus()
    }
    onMounted(() => {
      window.addEventListener('keydown', functionKey)
    })

    onUnmounted(() => {
      window.removeEventListener('keydown', functionKey)
    })

    return {
      page,
      isInvalidPage,
      onChange,
      firstItemNum,
      fistItemIndex,
      lastItemIndex,
      goToPrevPage,
      goToNextPage,
      handleKeyDown,
      focusPagination,
      handleFocus
    }
  }
})

export default Pagination
</script>

<style scoped lang="sass">
@import '@/style/css/common.sass'
.current
  display: grid
  grid-template-columns: 1fr auto
.current-page
  height: 32px
  width: 56px
  ::v-deep .v-input__control > .v-input__slot
    min-height: 32px !important
    height: 32px
  ::v-deep input
      text-align: center
.total-page, .icon, .items-display
  line-height: 32px
.total-page, .items-display
  font-size: 16px
.v-btn:not(.v-btn--round).v-size--default
  padding: 0
  min-width: 32px
  height: 32px
</style>
